import { Measure, WeightGrid } from '@wizeoo/akm-message-lib';
import { DATA_KIND } from '../../model/enums';
import { FunctionCoreEngine } from './function.core.engine';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { WizConstants } from '../../model/constants';
import { MeasureVO } from '../../vo/workingdata/measure.vo';
import { MeasMode, MeasModeItem } from './measure.mode.obj';

/**
 * Classe utilitaire pour les Measures & MeasureMode
 */
export class MeasureCoreEngine {

  // ######################################
  // refonte, gestion de la récursivité des ()
  // ######################################
  public static readonly NEO_SUB_PATTERN: RegExp = /\{\{[0-9]+\}\}/; // la regExp pour les parenthèses et fonctions remplacées
  public static readonly NEO_PARENTHESIS_PATTERN: RegExp = /\((.(?<!\())*?\)/; // la regExp pour les parenthèses sans '(' intermédiaire
  public static readonly NEO_FUNCTION_PATTERN: RegExp = /[A-Z]+\((.(?<!\())*?\)/; // la regExp pour les fonctions sans '(' intermédiaire
  /** le pattern pour détection des variables externes (sous la forme MEASURE#Mode) */
  public static readonly NEO_MM_PATTERN: RegExp = /[a-zA-Z]{1}[a-zA-Z0-9_-]+#[a-zA-Z]{1}/;
  /** un number */
  public static readonly NEO_NUMBER_PATTERN: RegExp = /[0-9]+[.]?[0-9]*/;
  /** opérateurs -+/* */
  public static readonly NEO_OPERATOR_PATTERN: RegExp = /\+|-|\*|\//; // [\+-/\*]{1}/;
  /** séparateur d'éléments de fonctions */
  public static readonly NEO_FUNCTION_SEPARATOR_PATTERN: RegExp = /[;]{1}/;
  /** comparateur pour fonctions logiques */
  public static readonly NEO_COMPARATOR_PATTERN: RegExp = /[<=>]{1,2}/;
  /** chaine de texte encadrée par " */
  // public static readonly NEO_TEXT_PATTERN: RegExp = /\".*?\"/;
  public static readonly NEO_TEXT_PATTERN: RegExp = /\"((\\\")*|.*?)\"/;

  /** regexp pour suppression des "" externes */
  public static readonly OUTERQUOTES: RegExp = /^"|"$/g;

  /** premier = de la formule */
  public static readonly NEO_FIRST_EQUAL_PATTERN: RegExp = /^[=]{1}/;

  // recherche d'un pattern de texte au global dans la formule pour exclure tous les textes
  public static readonly NEO_GLOBAL_TEXT_PATTERN: RegExp = new RegExp(MeasureCoreEngine.NEO_TEXT_PATTERN.source, 'g');


  // recherche d'un pattern MM au global dans la formule pour lister les dépendances
  public static readonly NEO_GLOBAL_MM_PATTERN: RegExp = new RegExp(MeasureCoreEngine.NEO_MM_PATTERN.source, 'g');

  // recherche des éléments à parenthèse
  public static readonly FIRST_ROUND_PATTERN: RegExp = new RegExp(MeasureCoreEngine.NEO_FUNCTION_PATTERN.source + '|' + MeasureCoreEngine.NEO_PARENTHESIS_PATTERN.source, 'g');
  // recherche d'accollades
  public static readonly INNER_SUB_PATTERN: RegExp = new RegExp(MeasureCoreEngine.NEO_SUB_PATTERN.source, 'g');

  // TODO extraire les chaines entre "" au 1er round

  // recherche des éléments unitaires
  public static readonly SECOND_ROUND_PATTERN: RegExp = new RegExp(
    MeasureCoreEngine.NEO_TEXT_PATTERN.source +
    '|' +
    MeasureCoreEngine.NEO_SUB_PATTERN.source +
    '|' +
    MeasureCoreEngine.NEO_FIRST_EQUAL_PATTERN.source +
    '|' +
    MeasureCoreEngine.NEO_MM_PATTERN.source +
    '|' +
    MeasureCoreEngine.NEO_OPERATOR_PATTERN.source +
    '|' +
    MeasureCoreEngine.NEO_COMPARATOR_PATTERN.source +
    '|' +
    MeasureCoreEngine.NEO_NUMBER_PATTERN.source,
    'g'
  ); // + '|' +
  // MeasureCoreEngine.NEO_TEXT_PATTERN.source, 'g');

  /** création d'une nouvelle mesure "light" (sans UUID car c'est couteux et dans certains cas nous n'en avons pas besoin) */
  public static LightFakeMeasureMode(fei: FeiVO, code: string, mode: string, formula: string): MeasMode {
    const meas: Measure = new Measure();
    meas.shortCode = code;
    meas.code = code;
    meas.mode = mode;
    meas.formulaS = formula;

    const computeMM: MeasMode = this.measureModeFromFormula(fei, meas, mode);
    return computeMM;
  }

  /**
   * création d'un MeasMode pour une constante
   *
   * @param measure Constante de vision concernée (Measure de type #C)
   * @return MeasMode MeasMode créé
   */
  public static measureModeFromConstant(fei: FeiVO, measure: Measure): MeasMode {
    const result: MeasMode = 
                { 
                  code: [measure.shortCode, WizConstants.MEASURE_MODE_CONSTANT].join(WizConstants.CONTEXT_SEPARATOR), // mc#mm
                  form: measure.formulaI, // formule
                  m: measure,
                  mc: measure.shortCode, // code mesure
                  mm: WizConstants.MEASURE_MODE_CONSTANT, // mode mesure
                  mmi: null,
                  r: null // result
                };

    // création du MMI
    result.mmi = this.buildMeasureModeItem(fei, result, result.form);
    result.mmi.fTer = true;
    result.mmi.fConst = true;
    if (measure.dataKind === DATA_KIND.STRING) {
      result.mmi.ftxt = true;
    }
    return result;
  }

  /**
   * création d'un MeasMode depuis un WeightGrid
   *
   * @param weightGrid Grille de poids concernée
   * @return MeasMode MeasMode créé
   */
  public static measureModeFromWeightGrid(fei: FeiVO, weightGrid: WeightGrid): MeasMode {
    
    let measure: Measure = new Measure();
    measure.code = weightGrid.code;
    measure.shortCode = weightGrid.code;
    measure.mode = WizConstants.MEASURE_MODE_WEIGHT_GRID;
    
    
    const result: MeasMode = 
    { 
      code: [measure.shortCode, WizConstants.MEASURE_MODE_WEIGHT_GRID].join(WizConstants.CONTEXT_SEPARATOR), // mc#mm
      form: measure.formulaI, // formule
      m: measure,
      mc: measure.shortCode, // code mesure
      mm: WizConstants.MEASURE_MODE_WEIGHT_GRID, // mode mesure
      mmi: null,
      r: null // result
    };

    // création du MMI qui contiendra la pile de calcul pour traitement récursif
    result.mmi = this.buildMeasureModeItem(fei, result, '1'); // on met 1 par défaut
    return result;
  }

  /**
   * création d'un MeasureMode (composition sur Measure avec #Mode (depuis Constant.MEASURE_MODE_xxx))
   *
   * @param measure La mesure concernée
   * @param mode    Mode à utiliser
   * @return MeasureMode MeasureMode créé
   */
  public static measureModeFromFormula(fei: FeiVO, measure: Measure, mode: string): MeasMode {
    const result: MeasMode = 
                { 
                  code: [measure.shortCode, mode].join(WizConstants.CONTEXT_SEPARATOR), // mc#mm
                  form: null, // formule
                  m: measure,
                  mc: measure.shortCode, // code mesure
                  mm: mode, // mode mesure
                  mmi: null,
                  r: null // result
                };

    switch (mode) {
      case WizConstants.MEASURE_MODE_FROM:
        result.form = measure.formulaF;
        break;
      case WizConstants.MEASURE_MODE_SUBMIT:
        result.form = measure.formulaS;
        break;
      case WizConstants.MEASURE_MODE_INTERACT:
        result.form = measure.formulaI;
        break;    
      case WizConstants.MEASURE_MODE_TO:
        result.form = measure.formulaT;
        break;
    }

    // collecte des dépendances sur d'autres mesures
    let cleanFormula: string = result.form || '';

    // on enlève toutes les chaines entre ""
    cleanFormula = cleanFormula.replace(MeasureCoreEngine.NEO_GLOBAL_TEXT_PATTERN, '');
   
    const cleanFormulaParts = cleanFormula.match(MeasureCoreEngine.NEO_GLOBAL_MM_PATTERN);
    if (cleanFormulaParts) {
      cleanFormulaParts.forEach((group: string) => {
        if (!result.deps) {
          result.deps = new Set();
        }
        result.deps.add(group);
      });
    }

    // création du MMI qui contiendra la pile de calcul pour traitement récursif
    result.mmi = this.buildMeasureModeItem(fei, result, result.form);

    // neutralisation des mesures éditables de type texte
    if (WizConstants.MEASURE_MODE_INTERACT === mode && measure.editable) {
      result.mmi.fTer = true;
      result.mmi.fConst = true;
      if (measure.dataKind === DATA_KIND.STRING) {
        result.mmi.ftxt = true;
      }
    }

    return result;
  }

  /**
   * Création d'une étape de calcul dans un MeasureMode le MMI contient une pile de mmi pour ventilation du calcul en éléments simple. A calculer en
   * récursif
   *
   * @param mm MeasMode à calculer
   * @param form  la formule "brute" à analyser. Elle sera finale (le mmi sera un opérateur ou une opérande) ou pas (formule, parenthèses)
   * @return mmi Etape de calcul créée
   */
  private static buildMeasureModeItem(fei: FeiVO, mm: MeasMode, form: string): MeasModeItem {
    const result: MeasModeItem = 
    {
      mm, // mesureMode porteur
      form, // formule brute
      r: null,  // result
      c: [] // sous-collection de mmi
    }
    
    // pas de formule, terminé
    if (!form) { 
      result.fTer = true;
      return result;
    }

    // génération des constantes
    if (mm.m.mode === WizConstants.MEASURE_MODE_CONSTANT) {
      result.r = form;
      result.fTer = true;

      if (mm.m.dataKind === DATA_KIND.STRING) {
        result.ftxt = true;
      }
      return result;
    }

    // ne commence PAS par = (donc une valeur constante)
    if (form.substring(0, 1) !== '=') {
      // console.log('texte brut', rawFormula);
      result.r = form;
      result.fTer = true;
      if (mm.m.dataKind === DATA_KIND.STRING) {
        result.ftxt = true;
      }
      return result;
    }

    // construction de la map des éléments gigognes (parenthèses et fonctions)
    // sous la forme  {x} = [la formule (éventuellement transformée avec de {{x}})forcément simple, la vraie formule, le mmi correspondant, créer au 2ème step]
    const partsMaps: Map<number, [string, string, MeasModeItem]> = new Map<number, [string, string, MeasModeItem]>();
    let idx: number = 0;
    let workingFormula: string = form; // on clone la formule brute, pour remplacer les éléments complexes par des {{x}}
    let rawFormulaArr: string[] = [];
    if (workingFormula) {
      // ### 1er traitement, remplacement des éléments à parenthèse ou fonction dans le partsMaps
      rawFormulaArr = workingFormula.match(MeasureCoreEngine.FIRST_ROUND_PATTERN);
      while (rawFormulaArr && rawFormulaArr.length > 0) {
        rawFormulaArr.forEach((group: string) => {
          idx++;
          // recherche des {{x}} dans le groupe (possible dès le 2ème passage, pour préservation de la vraie formule)
          const innerAccols: string[] = group.match(MeasureCoreEngine.INNER_SUB_PATTERN);
          let realFormula: string = group;
          if (innerAccols && innerAccols.length > 0) {
            // la sous formule contient une sous-formule. On alimente correctement sur "formule d'origine"
            innerAccols.forEach((innerGroup: string) => {
              const innerIdx = innerGroup.substring(2, innerGroup.length - 2);
              // console.log('inner', innerGroup, innerIdx);
              realFormula = realFormula.replace(innerGroup, partsMaps.get(Number(innerIdx))[1]); // et remplacement de la chaine correspondante dans la formule en cours
            });
          }

          partsMaps.set(idx, [group, realFormula, null]); // ajout de l'entrée dans le dico de sous-éléments
          workingFormula = workingFormula.replace(group, '{{' + idx + '}}'); // et remplacement de la chaine correspondante dans la formule en cours

          rawFormulaArr = workingFormula.match(MeasureCoreEngine.FIRST_ROUND_PATTERN); // et rebelote. les (()) sont devenus des ({{x}})
        });
      }

      // alimentation des MMI de la partsMaps pour futur remplacement dans les contents
      let subMMI: MeasModeItem;
      if (partsMaps.size > 0) { // Si on passe ici, c'est soit des parenthèses soit une fonction
        // s'il existe des sous-éléments, on créer leurs MMis

        let toBuildIdx: number = 1; // on va les construire dans l'ordre
        while (toBuildIdx <= idx) {


          const subWorkingFormula = partsMaps.get(toBuildIdx)[0]; // collecte de la sous-formule transformée
          const subRealFormula = partsMaps.get(toBuildIdx)[1]; // collecte de la sous-formule. C'est soit () soit Func()
          // console.log ('parts toBuildIdx', subWorkingFormula, subRealFormula, idx);
          subMMI = this.buildRawMMI(mm, subRealFormula);

          if (MeasureCoreEngine.NEO_FUNCTION_PATTERN.test(subWorkingFormula)) {
            // c'est soit une formule
            // console.log ('parts toBuildIdx fct way');
            subMMI.fFunc = true;
            subMMI.funcCode = subWorkingFormula.split(/\(.*\)/)[0];
            if(!!fei) { // pas de contrôle sur FEI pour les widgets FDV
              FunctionCoreEngine.checkFunctionExist(fei.model, subMMI); // contrôle de validité & type (aggrégat)
            }
            
            if (!subMMI.isErr) {
              // construction des enfants
              this.buildSubMMI(fei, subMMI, subWorkingFormula, partsMaps);
            }

          } else if (MeasureCoreEngine.NEO_PARENTHESIS_PATTERN.test(subRealFormula)) {
            // console.log ('parts toBuildIdx parenthesis way');
            // soit une parenthèse
            subMMI.fSis = true;
            this.buildSubMMI(fei, subMMI, subWorkingFormula, partsMaps);
          } else {
            subMMI.isErr = true;
            // console.error('unknown subPart');
          }

          partsMaps.get(toBuildIdx)[2] = subMMI;
          toBuildIdx++; // pour ajout du mmi (et de ses enfants...) dans partsMMiMaps
        }
      }

      // suppression du 1er =
      workingFormula = workingFormula.replace(this.NEO_FIRST_EQUAL_PATTERN, '');
      // if(workingFormula.match(this.NEO_FIRST_EQUAL_PATTERN))

      // if (workingFormula === '{{2}}') {
      //   console.log('oups', workingFormula, partsMaps);
      // }
      this.buildSubMMI(fei, result, workingFormula, partsMaps); // construction du mmi principal
    }
    return result;
  }

  /**
   * contrôle de cohérence des paramètres
   * alternance d'opérateur / opérandes
   * existance des mesures. Pas de mesure 'self' dans la mesure
   * TODO contrôle des paramètres des fonctions
   * @param mmi le MeasModeItem
   * @param partsMaps les sous-éléments
   */
  private static checkIntegrity(mmi: MeasModeItem, partsMaps: Map<number, [string, string, MeasModeItem]>): void {
    // controle de cohérence. Les groups doivent reconstituer la chaine complète

    // je suis une chaîne de texte, aucun traitement à réaliser
    // // on enlève toutes les chaines entre ""
    // cleanFormula = cleanFormula.replace(MeasureCoreEngine.NEO_GLOBAL_TEXT_PATTERN, '');
    // // console.log('clean formula', result.formula, cleanFormula);

    // const cleanFormulaParts = cleanFormula.match(MeasureCoreEngine.NEO_GLOBAL_MM_PATTERN);
    if (!!mmi.form) {

      const cleanFormula = mmi.form.replace(MeasureCoreEngine.OUTERQUOTES, '');
      // const cleanFormula = mmi.rawFormula.replace(MeasureCoreEngine.NEO_GLOBAL_TEXT_PATTERN, '');
      // console.log('fullTextParam ok', cleanFormula, mmi.rawFormula);
      if (!cleanFormula) { // on a tout supprimé, c'est bon
        // console.log("full text", mmi.rawFormula);
        mmi.ftxt = true;
        mmi.fTer = true;
        // let rx = /((?:^|[^\\])(?:\\{2})*)"/g;
        mmi.r = mmi.form.replace(MeasureCoreEngine.OUTERQUOTES, '');
        // mmi.result = mmi.rawFormula.replace(new RegExp('"', 'g'), '');
        // console.log("full text checkIntegrity", mmi.rawFormula, mmi.result);
        mmi.isErr = false;
        return;
      }
    }

    // if(MeasureCoreEngine.NEO_GLOBAL_TEXT_PATTERN.test(mmi.rawFormula)) {
    //   console.log('fullTextParam ok', mmi.rawFormula);
    //   return;
    // }

    // peu importe les espaces, le moteur sait gérer
    const rawNoSpace: string = mmi.form.replace(/\s+/g, '').replace(this.NEO_FIRST_EQUAL_PATTERN, '');
    let partJoin: string = '';
    if (!!mmi.c) {
      if (mmi.fFunc) {
        partJoin = mmi.c.map((subMMi: MeasModeItem) => subMMi.form).join(';');
      } else {
        partJoin = mmi.c.map((subMMi: MeasModeItem) => subMMi.form).join('');
      }
    }
    if (mmi.fSis || mmi.fFunc) {
      partJoin = '(' + partJoin + ')';
    }
    if (mmi.fFunc) {
      partJoin = mmi.funcCode + partJoin;
    }

    partJoin = partJoin.replace(/\s+/g, '');
    if (rawNoSpace !== partJoin) {
      console.log('Unknown part Error', rawNoSpace, partJoin);
      mmi.isErr = true;
      mmi.errC = FunctionCoreEngine.UNKNOW_PART_ERROR;
      mmi.mm.isErr = true;
      mmi.mm.errC = FunctionCoreEngine.UNKNOW_PART_ERROR;
      mmi.mm.errS = mmi.form;
      // console.log('Unknown part Error', mmi.content, mmi.rawFormula);
      return;
    }

    // controle de l'alternance des operateurs / operandes
    if (mmi.form && mmi.c && !mmi.fFunc) {
      // pas pour les fonctions. On va les contrôles spécifiquement
      let currentKindSign: number = 0; // on va passer 1 pour les opérateurs, -1 pour le reste (tout. le reste retourne une valeur)
      // si on a 2x le même à la suite, problème d'enchainement
      // if (mmi.rawFormula === '=+20-(-45*2)+(-BOB#I)(12)') {
      //   console.log('me');
      // }

      // une parenthèse avec un seul enfant de type opérateur / comparateur
      if (
        mmi.fSis &&
        mmi.c[0].c.length === 1 &&
        (mmi.c[0].c[0].fOper || mmi.c[0].c[0].fComp)
      ) {
        console.log('OP alone Error', mmi.c, mmi.form);
        mmi.isErr = true;
        mmi.r = FunctionCoreEngine.OPERATOR_ERROR;
        mmi.errC = FunctionCoreEngine.OPERATOR_ERROR;
        mmi.mm.isErr = true;
        mmi.mm.errC = FunctionCoreEngine.OPERATOR_ERROR;
        mmi.mm.errS = mmi.form;
        return;
      }

      mmi.c.forEach((subMMi: MeasModeItem) => {
        // console.log('check', mmi.rawFormula, mmi.content, subMMi);
        // if(subMMi.flagOperand) {
        //   console.log("flagOperand", subMMi.rawFormula);
        // }

        if (subMMi.fOper || subMMi.fComp) {
          if (currentKindSign === 1) {
            console.log('OP alternate Error', mmi.c, mmi.form);
            mmi.isErr = true;
            mmi.r = FunctionCoreEngine.OPERATOR_ERROR;
            mmi.errC = FunctionCoreEngine.OPERATOR_ERROR;
            mmi.mm.isErr = true;
            mmi.mm.errC = FunctionCoreEngine.OPERATOR_ERROR;
            mmi.mm.errS = mmi.form;
          }
          currentKindSign = 1;
        } else {
          if (subMMi.fRand && subMMi.form === mmi.mm.code) {
            // measure self dans la mesure
            // ByPass INHERIT, SPLIT et COLLECT
            // if (  mmi.mm?.form?.indexOf('INHERIT') > 0 ||
            //       mmi.mm?.form?.indexOf('SPLIT') > 0 ||
            //       mmi.mm?.form?.indexOf('ANCESTOR') > 0 ||
            //       mmi.mm?.form?.indexOf('SUBCOLLECT') > 0 ||
            //       mmi.mm?.form?.indexOf('COLLECT') > 0) {
            //   // améliorer le contrôle en balayant récursivement les contexts
            // } else {
              console.log('SELF Error', mmi.c, subMMi);
              mmi.isErr = true;
              mmi.r = FunctionCoreEngine.CIRCULAR_ERROR;
              mmi.errC = FunctionCoreEngine.CIRCULAR_ERROR;
              mmi.mm.isErr = true;
              mmi.mm.errC = FunctionCoreEngine.CIRCULAR_ERROR;
              mmi.mm.errS = mmi.form;
            //}
          }

          if (currentKindSign === -1) {
            console.log('VAL Error', mmi.c, subMMi);
            mmi.isErr = true;
            mmi.r = FunctionCoreEngine.VALUE_ERROR;
            mmi.errC = FunctionCoreEngine.VALUE_ERROR;
            mmi.mm.isErr = true;
            mmi.mm.errC = FunctionCoreEngine.VALUE_ERROR;
            mmi.mm.errS = mmi.form;
          }
          currentKindSign = -1;
        }
      });

      if (currentKindSign === 1) {
        // termine par un opérateur / comparateur, anormal
        console.log('OP ending Error', mmi.c, mmi.form);
        mmi.isErr = true;
        mmi.r = FunctionCoreEngine.OPERATOR_ERROR;
        mmi.errC = FunctionCoreEngine.OPERATOR_ERROR;
        mmi.mm.isErr = true;
        mmi.mm.errC = FunctionCoreEngine.OPERATOR_ERROR;
        mmi.mm.errS = mmi.form;
      }
    }
  }

  /**
   * alimentation des mmis enfant d'un MMI (soit global, soit issus d'une () ou d'une funct())
   * @param mmi à compléter
   * @param workingFormula sa formule "brute" simplifiée (pas de gigognes, des {{x}})
   * @param parts le tableau d'éléments gigognes
   */
  private static buildSubMMI(fei: FeiVO, mmi: MeasModeItem, workingFormula: string, parts: Map<number, [string, string, MeasModeItem]>): void {

    let child: MeasModeItem; // le mmi que l'on va construire pour alimenter "maman"

    // 3 cas possibles: fonction, () ou formule "simple (non gigogne)"

    // ###################################
    //  fonctions
    if (mmi.fFunc) {
      const functionBody: string = workingFormula.split(/^[A-Z]*\(|\)$/)[1]; // extraction du corps de la fonction (entre les parenthèses externes)
      const params: string[] = functionBody?.split(FunctionCoreEngine.FUNCTION_SEPARATOR) || []; // les params de la fonction

      // contrôle de cohérence des paramètres
      if (FunctionCoreEngine.checkFunctionParameters(fei.model, mmi, params)) {
        
        // on marque les measMode traductibles (uselocale = true)
        FunctionCoreEngine.checkTranslatableFunction(fei.model, mmi, params);
        
        // construction des paramètres
        let paramIdx: number = 1;
        params.forEach((param: string) => {
          child = this.buildRawMMI(mmi.mm, param); // attention, le group est peut-être un {{x}}, à remplacer...
          if (!param || param.length === 0) {
            mmi.isErr = true;
            mmi.mm.isErr = true;
            mmi.r = FunctionCoreEngine.EMPTY_FUNCTION_PARAM_ERROR;
            mmi.errC = FunctionCoreEngine.EMPTY_FUNCTION_PARAM_ERROR;
            mmi.mm.errC = FunctionCoreEngine.EMPTY_FUNCTION_PARAM_ERROR;
            console.log('empty param Error', mmi.c, mmi.form);
            mmi.mm.errS = paramIdx.toString();
          } else {
            this.buildSubMMI(fei, child, param, parts);
            if (child.c?.length > 0) {
              child.form = child.c.map((item) => item.form).join(''); // fix mauvais remplacement des {{x}} sur Func(Func(Measure))
            }
          }
          mmi.c.push(child);
          paramIdx++;
        });

        // alimentation des dépendances de niveaux
        FunctionCoreEngine.getStepLevelDependencies(fei, mmi, params);
        // alimentation des dépendances sur des éléments externes
        FunctionCoreEngine.getCrossDependencies(fei, mmi, params);

      }
      // sinon rien, le checkFunctionParameters a taggé l'erreur
    }
    // ###################################
    //  parenthèse
    else if (mmi.fSis) {
      // construction d'un enfant sans les parenthèses
      child = this.buildRawMMI(mmi.mm, mmi.form.substring(1, mmi.form.length - 1));
      this.buildSubMMI(fei, child, workingFormula.substring(1, workingFormula.length - 1), parts);
      mmi.c.push(child);
    }
    // ###################################
    //  éléments "simple" (pas gigogne)
    else {
      // une sous-chaine classique
      const groups: string[] = workingFormula.match(MeasureCoreEngine.SECOND_ROUND_PATTERN);
      // console.log('subPattern', groups);
      if (groups?.length > 0) {

        groups.forEach((group: string) => {
          child = this.buildRawMMI(mmi.mm, group); // attention, le group est peut-être un {{x}}, à remplacer...
          this.setMmiKind(fei, child, group, parts);
          // ici, si l'enfant est = {{x}}, remplacement dans le workingFormula
          if (MeasureCoreEngine.NEO_SUB_PATTERN.test(group)) {
            const innerIdx = group.substring(2, group.length - 2);
            mmi.form = mmi.form.replace(group, parts.get(Number(innerIdx))[1]);
          }

          // LMA patch prb strings
          if (child.form === mmi.form && child.ftxt) {
            // console.log('child inutile', mmi.rawFormula, mmi.measureMode.measure.shortCode);
            mmi.ftxt = true;
            mmi.fTer = true;
            mmi.r = mmi.form.replace(MeasureCoreEngine.OUTERQUOTES, '');
            // console.log('child inutile', mmi.rawFormula, mmi.result);
            mmi.isErr = false;
          }
          mmi.c.push(child);

        });
      }
    }

    // contrôle de cohérence de la chaine. Les opérateurs doivent alterner avec les valeur
    this.checkIntegrity(mmi, parts);
  }

  /**
   * construction d'un mmi brut
   * @param mm le mm source
   * @param form la formule (vraie)
   */
  public static buildRawMMI(mm: MeasMode, form: string): MeasModeItem {
    const result: MeasModeItem = 
    {
      c: [], // sous-contenu de calcul
      mm, // mesureMode porteur
      form, // formule brute
      r: null // result
    }
    return result;
  }

  /**
   * Construction d'une étape de calcul "simple"
   * enfant) Si la formule contient une fonction, on va découper plus finement Si la formule contient une fonction inconnue, on bascule en erreur
   *
   * @param mmi à calculer
   * @param rawFormula  La formule à analyser
   * @param parts, les {{x}}
   */
  private static setMmiKind(
    fei: FeiVO,
    result: MeasModeItem,
    rawFormula: string,
    parts: Map<number, [string, string, MeasModeItem]>): void {


    if (MeasureCoreEngine.NEO_SUB_PATTERN.test(rawFormula)) {
      // on reconnait un {{x}}, on remplace le rawFormula et on lui passe le contenu du parts
      const innerIdx = rawFormula.substring(2, rawFormula.length - 2);

      result.fFunc = parts.get(Number(innerIdx))[2].fFunc;
      result.fSis = parts.get(Number(innerIdx))[2].fSis;
      result.funcCode = parts.get(Number(innerIdx))[2].funcCode;
      result.form = parts.get(Number(innerIdx))[1]; // et remplacement de la chaine correspondante dans la formule en cours
      result.c = parts.get(Number(innerIdx))[2].c; // et prise des enfants du mmi
    }
    else if (MeasureCoreEngine.NEO_TEXT_PATTERN.test(rawFormula)) {
      // un texte pour affichage direct
      // console.log('TEXT_PATTERN MCE', rawFormula, result.measureMode.code);
      result.ftxt = true;
      result.fTer = true;
      result.r = rawFormula.replace(MeasureCoreEngine.OUTERQUOTES, '');
      // console.log('child neoDefineMeasureModeItem', rawFormula, result.result);
    }

    else if (MeasureCoreEngine.NEO_MM_PATTERN.test(rawFormula)) {
      // une variable de type MEASURE#Mode
      const partMeasure: string = rawFormula.split(WizConstants.CONTEXT_SEPARATOR)[0];
      const partMode: string = rawFormula.split(WizConstants.CONTEXT_SEPARATOR)[1];
      let existMeasure: MeasureVO;
      if (WizConstants.MEASURE_MODE_ALL.indexOf(partMode) < 0) {
        result.isErr = true;
        result.mm.isErr = true;
        result.r = FunctionCoreEngine.UNKNOW_MODE_ERROR;
        result.errC = FunctionCoreEngine.UNKNOW_MODE_ERROR;
        result.mm.errC = FunctionCoreEngine.UNKNOW_MODE_ERROR;
        console.log('Unknown mode Error', result.c, rawFormula);
        result.mm.errS = rawFormula;
      } else {
        // contrôle de cohérence avec les mesures existantes
        existMeasure = fei.model.measureEngine.getMeasure(fei.model.visionCode, 0, partMeasure, partMode === WizConstants.MEASURE_MODE_WEIGHT_GRID);
        if (!existMeasure || existMeasure === undefined) {
          // contrôle d'inexistance de la mesure
          result.isErr = true;
          result.mm.isErr = true;
          result.r = FunctionCoreEngine.UNKNOW_MEASURE_ERROR;
          result.errC = FunctionCoreEngine.UNKNOW_MEASURE_ERROR;
          result.mm.errC = FunctionCoreEngine.UNKNOW_MEASURE_ERROR;
          console.log('Unknown measure Error', result.c, rawFormula);
          result.mm.errS = rawFormula;
        } else {
          // la mesure existe, controle des références circulaires
          const deps: Map<string, MeasMode> = fei.model.measureEngine.measureModeFullDependencies(fei, rawFormula);
          if (deps.has(result.mm.code)) {
            result.isErr = true;
            result.mm.isErr = true;
            result.r = FunctionCoreEngine.CIRCULAR_ERROR;
            result.errC = FunctionCoreEngine.CIRCULAR_ERROR;
            result.mm.errC = FunctionCoreEngine.CIRCULAR_ERROR;
            result.mm.errS = rawFormula;
            console.log('circular dependency Error', result.c, rawFormula);
          }
        }
      }

      // NON, sinon pas de calcul
      // if(existMeasure?.dataKind === DATA_KIND.STRING) {
      //   result.flagText = true;
      // }
      result.fRand = true;
      result.fTer = true;
    } else if (MeasureCoreEngine.NEO_OPERATOR_PATTERN.test(rawFormula)) {
      // on reconnait un opérateur +-/*
      // console.log('OPERATOR_PATTERN', rawFormula);
      result.fOper = true;
      result.fTer = true;
    } else if (MeasureCoreEngine.NEO_COMPARATOR_PATTERN.test(rawFormula)) {
      // on reconnait un comparateur <=>
      // console.log('COMPARATOR_PATTERN', rawFormula);
      result.fComp = true;
      result.fTer = true;
    }
    else if (MeasureCoreEngine.NEO_NUMBER_PATTERN.test(rawFormula)) {
      // un nombre
      result.fConst = true;
      result.fTer = true;
    } else {
      // le fragment égale toute la rawFormula, mais ne correspond à rien ???
      result.ftxt = true;
      result.fTer = true;
      result.isErr = true;
      // console.log('TEXT_PATTERN MCE unkn', rawFormula, result.measureMode.code);
      result.errC = FunctionCoreEngine.UNKNOW_PART_ERROR;
      result.mm.isErr = true;
      result.mm.errC = FunctionCoreEngine.UNKNOW_PART_ERROR;
      console.log('Unknown part Error', result.c, result.form);
      result.mm.errS = result.form;
    }
  }

  /**
   * ajout d'une mesure à une map (Model.getInstance().measureMap)
   */
  public static addMeasureToMap(map: Map<string, Map<number, Map<string, MeasureVO>>>, meas: MeasureVO): void {
    if (!map.has(meas.vision)) {
      map.set(meas.vision, new Map<number, Map<string, MeasureVO>>());
    }
    if (!map.get(meas.vision).has(meas.stepLevel)) {
      map.get(meas.vision).set(meas.stepLevel, new Map<string, MeasureVO>());
    }
    map.get(meas.vision).get(meas.stepLevel).set(meas.shortCode, meas);
  }

  /**
   * suppression d'une mesure à une map (Model.getInstance().measureMap)
   */
  public static removeMeasureFromMap(map: Map<string, Map<number, Map<string, MeasureVO>>>, meas: MeasureVO): void {
    if (map.has(meas.vision) && map.get(meas.vision).has(meas.stepLevel)) {
      map.get(meas.vision).get(meas.stepLevel).delete(meas.shortCode);
    }
  }

}
